import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

document.addEventListener('DOMContentLoaded', () => {
  const videos = Array.from(document.querySelectorAll('.js-card-slider-video'));

  /**
   * Attempts to play a video and handles errors
   * @param {HTMLVideoElement} video - Video element to play
   * @returns {Promise<void>}
   */
  async function tryPlayVideo(video) {
    try {
      await video.play();
    } catch (error) {
      console.warn('Video oynatma hatası:', error);
    }
  }

  /**
   * Loads video sources and initiates playback
   * @param {HTMLVideoElement} video - Video element to load
   */
  function loadVideo(video) {
    const sources = video.querySelectorAll('source');

    sources.forEach((source) => {
      if (source.dataset.src) {
        // Create a new source element to avoid modifying function parameter
        const newSource = source.cloneNode();
        newSource.src = source.dataset.src;
        source.parentNode.replaceChild(newSource, source);
      }
    });

    video.load();
    video.setAttribute('data-loaded', 'true');
    tryPlayVideo(video);
  }

  /**
   * Checks if element is visible in viewport
   * @param {HTMLElement} el - Element to check
   * @returns {boolean} True if element is in viewport
   */
  function isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * Creates and configures IntersectionObserver for videos
   * @type {IntersectionObserver}
   */
  const videoObserver = new IntersectionObserver(
    /**
     * Callback for intersection changes
     * @param {IntersectionObserverEntry[]} entries - Intersection entries
     */
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        if (entry.isIntersecting) {
          // Video is visible
          if (!video.hasAttribute('data-loaded')) {
            loadVideo(video);
          } else if (video.paused && video.hasAttribute('data-loaded')) {
            tryPlayVideo(video);
          }
        } else if (!video.paused) {
          // Video is not visible - pause it if playing
          video.pause();
        }
      });
    },
    {
      threshold: 0.3, // Video needs to be at least 30% visible
    },
  );

  // Handle tab visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Page is not visible, pause all videos
      videos.forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
      });
    } else {
      // Page is visible again, check what videos should be playing
      videos.forEach((video) => {
        if (video.hasAttribute('data-loaded') && isElementInViewport(video)) {
          tryPlayVideo(video);
        }
      });
    }
  });

  /**
   * Initializes video observers and ScrollTrigger
   */
  videos.forEach((video) => {
    video.pause();
    videoObserver.observe(video);

    // Keep ScrollTrigger for backward compatibility
    ScrollTrigger.create({
      trigger: video,
      markers: false,
      start: 'center bottom',
      end: 'bottom top',
      /**
       * Callback when video enters viewport
       */
      onEnter: () => {
        if (!video.hasAttribute('data-loaded')) {
          loadVideo(video);
        }
      },
    });
  });
});
